
import { BuildingComplex, Earth, HouseHeart, Layers, Radar, Radar as Radar2, Signpost, Telescope } from 'lucide-react';
import GoogleMapReact from 'google-map-react';
import googlePinIcon from 'images/google-buttons/google_pin.svg';
import MapPinBlack from 'images/icon/map/pin_black.svg';
import MapPinWhite from 'images/icon/map/pin_white.svg';
import Close from 'images/icons/orders/close.svg';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import useSupercluster from 'use-supercluster';
import { AFrequencyEventType } from '../../atoms/global/frequency';
import { AProfile, AUsers } from '../../atoms/global/users';
import Avatar from '../../components_v2/avatar/Avatar';
import LassoIcon from '../../styles/custom-icons/lasso';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { PrimaryColor, FilterPrimaryColor, FilterDarkGrey2, RedSidely, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { useFunctionState } from '../../utils/customHooks';
import { NoMarginP } from '../authorization/users/Members';
import { CompanyStatus } from '../client-companies/model/Model';
import { LoadingStateEnum } from '../import/model';
import { Owner } from '../orders/model/Model';
import PermissionContext from '../permissions/PermissionContext';
import { ModalState } from '../products/model';
import { FlexDiv } from '../products/style';
import ClusterIcon from './cluster';
import { MapSettingsContext, MapSettingsContextType, MapSettingsProvider } from './context';
import { useBoundariesPolys, useUsersAddresses, useUsersPoly } from './hooks';
import { activateMapListeners, onLassoClick } from './lasso';
import MapFullScreenButton from './mapButtons/fullScreen';
import MapSearch from './mapButtons/search';
import { MapDot, MapPinComponent } from './MapPin';
import { CompaniesInLasso, FetchKey, MinMax, MinMaxAdditionalColumn } from './MapView';
import { ComponentLoader } from './modalRight/ModalCalendar';
import { Position } from './modalRight/model';
import { Cluster, ClusterProperties, GooglePlaceItem, InitialMapRow, isCluster, MapCompany, MapData, Point } from './model';
import { MAP_STYLE_LIST, MapStyleDefault } from './style';

const GOOGLE_KEY = {
	key: 'AIzaSyCYkQC599NV99WgBGvW2RHABKHXowlisgw'
};

export const Marker = ({ children, lat: _lat, lng: _lng }) => children;
const defaultCenter = { lat: 48.85356482880834, lng: 2.348096962645894 };
const defaultZoom = 6;
const initialPositionZoom = 15;

interface MapState {
	zoom: number
	bounds: number[]
	center: Position
}

export const MapButton = styled(DefaultButton) <{ marginRight?: boolean, marginTop?: boolean }>`
	font-size: 14px;
	margin: 0;
	// Map style
	background-color: ${SurfaceColor};
	border: 2px solid ${SurfaceColor};
	box-shadow: 0 2px 6px color-mix(in oklab, ${SidelyBlack} 30%, transparent);
	padding: 5px;
	text-align: center;
	height: 40px;
	width: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	${({ marginRight }) => marginRight ? 'margin-right: 10px;' : ''}
	${({ marginTop }) => marginTop ? 'margin-top: 10px;' : ''}

`;

const LayerGrid = styled.div`
	display: grid;
  	grid-template-columns: repeat(2, 75px);
	grid-gap: 10px;
	width: 100%;
`;

const LayerTileWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-flow: column;
	gap: 3px;
	cursor: pointer;
`;

const LayerTile = styled.div<{ selected: boolean }>`
	width: 50px;
	aspect-ratio: 1/1;
	border: 1px solid ${({ selected }) => selected ? PrimaryColor : SidelyBlack};
	border-radius: 10px;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const CrossClose = styled.img`
	width: 17px;
	aspect-ratio: 1;
	cursor: pointer;
	position: absolute;
	top: 10px;
	right: 10px;
	filter: ${FilterDarkGrey2};
	&:hover {
		filter: ${FilterPrimaryColor};
	}
`;

const MAP_BASIC_BUTTON_STYLE: React.CSSProperties = {
	backgroundColor: SurfaceColor,
	border: `2px solid ${SurfaceColor}`,
	boxShadow: `0 2px 6px color-mix(in oklab, ${SidelyBlack} 30%, transparent)`,
	cursor: 'pointer',
	padding: '5px',
	textAlign: 'center',
	width: '40px',
	height: '40px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center'
};

const MAP_ROUND_BUTTON_STYLE: React.CSSProperties = {
	...MAP_BASIC_BUTTON_STYLE,
	borderRadius: '50%',
	margin: '10px',
};

const MAP_SQUARE_BUTTON_STYLE: React.CSSProperties = {
	...MAP_BASIC_BUTTON_STYLE,
	borderRadius: '3px',
	marginRight: '10px',
};

let urchinGeoJsonPath: any | null;
let urchinPoints: any[] | null;

function applyStyleToDiv(div: HTMLDivElement, style: React.CSSProperties) {
	for (const [key, value] of Object.entries(style)) {
		div.style[key] = value;
	}
}


type MapSettingsTile = {
	child: React.ReactNode,
	title: string,
	selected: boolean,
	id: string,
	onClick?: React.MouseEventHandler<HTMLDivElement>,
};

function MapLayerCategory(props: { title: string, tiles: MapSettingsTile[] }) {
	return <FlexDiv width='100%' flow='column' align='flex-start'>
		<h6>{props.title}</h6>
		<LayerGrid>
			{props.tiles.map(tile => (
				<LayerTileWrapper key={tile.id} onClick={tile.onClick}>
					<LayerTile selected={tile.selected}>{tile.child}</LayerTile>
					<p style={{ margin: '0', color: tile.selected ? PrimaryColor : SidelyBlack }}>{tile.title}</p>
				</LayerTileWrapper>
			))}
		</LayerGrid>
	</FlexDiv>;
}

export type ReactStateType<T> = [T, React.Dispatch<React.SetStateAction<T>>];
export type NullableReactStateType<T> = ReactStateType<T | undefined>

function MapLayersSettings(props: { settingContext: MapSettingsContextType }) {
	const {
		userPolyModeHook,
		userAdressesModeHook,
		strokeBoundaryModeHook,
		fillBoundaryModeHook,
		displayCompaniesHook,
		mapStyleHook,
	} = props.settingContext;
	const [isOpen, setIsOpen] = React.useState(false);
	const [userPolyMode_, setUserPolyMode_] = userPolyModeHook;
	const [userPolyMode, setUserPolyMode] = useFunctionState(userPolyMode_, undefined, (newState) => setUserPolyMode_(newState));
	const [mapStyle_, setMapStyle_] = mapStyleHook;
	const [mapStyle, setMapStyle] = useFunctionState(mapStyle_, undefined, (newState) => setMapStyle_(newState));
	const [userAdressesMode_, setUserAdressesMode_] = userAdressesModeHook;
	const [userAdressesMode, setUserAdressesMode] = useFunctionState(userAdressesMode_, undefined, (newState) => setUserAdressesMode_(newState));
	const [displayCompanies_, setDisplayCompanies_] = displayCompaniesHook;
	const [displayCompanies, setDisplayCompanies] = useFunctionState(displayCompanies_, undefined, (newState) => setDisplayCompanies_(newState));
	const [strokeBoundaryMode_, setStrokeBoundaryMode_] = strokeBoundaryModeHook;
	const [strokeBoundaryMode, setStrokeBoundaryMode] = useFunctionState(strokeBoundaryMode_, undefined, (newState) => setStrokeBoundaryMode_(newState));
	const [fillBoundaryMode_, setFillBoundaryMode_] = fillBoundaryModeHook;
	const [fillBoundaryMode, setFillBoundaryMode] = useFunctionState(fillBoundaryMode_, undefined, (newState) => setFillBoundaryMode_(newState));


	const swapMapStyle = (styleIndex: number) => () => setMapStyle((_) => {
		localStorage.setItem('mapStyle', styleIndex.toString());
		return styleIndex;
	});


	if (!isOpen) return <MapButton marginRight marginTop onClick={() => setIsOpen(true)}><Layers height={24} width={24} color={SidelyBlack} /></MapButton>;
	const style: React.CSSProperties = {
		...MAP_SQUARE_BUTTON_STYLE,
		width: 'unset',
		height: 'fit-content',
		alignItems: 'start',
		padding: '10px',
		borderRadius: '10px',
		cursor: 'unset',
		position: 'relative',
		zIndex: 99,
		marginTop: 10
	};
	return <FlexDiv style={style} flow='column' gap='20px'>
		<CrossClose src={Close} onClick={() => { setIsOpen(false); }} />
		<MapLayerCategory title={translateToString('user_details')} tiles={[
			{ child: <HouseHeart width='50%' height='50%' color={userAdressesMode ? PrimaryColor : SidelyBlack} />, title: translateToString('address'), selected: userAdressesMode, id: 'address', onClick: () => setUserAdressesMode(userAdressesMode => !userAdressesMode) },
			{
				child: <Earth width='50%' height='50%' color={userPolyMode ? PrimaryColor : SidelyBlack} />, title: translateToString('sector'), selected: userPolyMode, id: 'user-regions', onClick: () => {
					setFillBoundaryMode(undefined);
					setUserPolyMode(userPolyMode => !userPolyMode);
				}
			},
			{
				child: <Radar width='50%' height='50%' color={fillBoundaryMode === 'region' ? PrimaryColor : SidelyBlack} />, title: translateToString('region'), selected: fillBoundaryMode === 'region', id: 'region', onClick: () => {
					setUserPolyMode(false);
					setFillBoundaryMode(boundaryMode => boundaryMode === 'region' ? undefined : 'region');
				}
			},
			{
				child: <Radar2 width='50%' height='50%' color={fillBoundaryMode === 'department' ? PrimaryColor : SidelyBlack} />, title: translateToString('department'), selected: fillBoundaryMode === 'department', id: 'department', onClick: () => {
					setUserPolyMode(false);
					setFillBoundaryMode(boundaryMode => boundaryMode === 'department' ? undefined : 'department');
				}
			},

		]} />
		<MapLayerCategory title={translateToString('frontiers')} tiles={[
			{ child: <Radar width='50%' height='50%' color={strokeBoundaryMode === 'region' ? PrimaryColor : SidelyBlack} />, title: translateToString('region'), selected: strokeBoundaryMode === 'region', id: 'region', onClick: () => setStrokeBoundaryMode(boundaryMode => boundaryMode === 'region' ? undefined : 'region') },
			{ child: <Radar2 width='50%' height='50%' color={strokeBoundaryMode === 'department' ? PrimaryColor : SidelyBlack} />, title: translateToString('department'), selected: strokeBoundaryMode === 'department', id: 'department', onClick: () => setStrokeBoundaryMode(boundaryMode => boundaryMode === 'department' ? undefined : 'department') },
		]} />
		<MapLayerCategory title={translateToString('map_type')} tiles={[
			{ child: <Signpost height='50%' width='50%' color={mapStyle === 0 ? PrimaryColor : SidelyBlack} />, title: translateToString('general.map'), selected: mapStyle === 0, id: 'map', onClick: swapMapStyle(0) },
			{ child: <Telescope height='50%' width='50%' color={mapStyle === 1 ? PrimaryColor : SidelyBlack} />, title: translateToString('satelite'), selected: mapStyle === 1, id: 'satellite', onClick: swapMapStyle(1) }
		]} />
		<MapLayerCategory title={translateToString('map_datas')} tiles={[
			{ child: <BuildingComplex height='50%' width='50%' color={displayCompanies ? PrimaryColor : SidelyBlack} />, title: translateToString('companies'), selected: displayCompanies, id: 'map', onClick: () => setDisplayCompanies(displayCompanies => !displayCompanies) },
		]} />
	</FlexDiv>;
}

function createLayersButton(settingContext: MapSettingsContextType): HTMLDivElement {
	const layersDiv = document.createElement('div');
	ReactDOM.render(<MapLayersSettings settingContext={settingContext} />, layersDiv);
	return layersDiv;
}

function createCustomFullscreenButton(map: any): HTMLDivElement {
	const fullScreenDiv = document.createElement('div');
	ReactDOM.render(<MapFullScreenButton map={map} />, fullScreenDiv);
	return fullScreenDiv;
}

function createSearchButton(map: any, setGooglePlaces: (places: GooglePlaceItem[]) => void, convertCompany?: (data: GooglePlaceItem[]) => void): HTMLDivElement {
	const div = document.createElement('div');
	ReactDOM.render(<MapSearch map={map} setGooglePlaces={setGooglePlaces} convertCompany={convertCompany} />, div);
	return div;
}

function createMapButton(controlDiv: HTMLDivElement, callBack: (p: { button: HTMLDivElement, image: HTMLImageElement }) => () => void, baseImage: string | React.ReactNode, style: React.CSSProperties, title: string) {
	// Set CSS for the control border.
	const controlUI = document.createElement('div');
	applyStyleToDiv(controlUI, style);
	controlUI.title = title;
	controlDiv.appendChild(controlUI);

	// Set CSS for the control interior.
	let controlImg;
	if (typeof baseImage === 'string') {
		controlImg = document.createElement('img');
		controlImg.src = baseImage;
		controlImg.style.width = '24px';
		controlImg.style.height = '24px';
		controlUI.appendChild(controlImg);
	} else {
		controlImg = document.createElement('div');
		ReactDOM.render(baseImage, controlImg);
		controlUI.appendChild(controlImg);
	}

	// Change Style
	controlUI.addEventListener('click', callBack({ button: controlUI, image: controlImg }));
}

export type UserCenter = {
	id: number;
	center: Position;
};

type MapProps = {
	points: Point[],
	tabType: FetchKey,
	companiesInLasso: CompaniesInLasso,
	statuses: CompanyStatus[],
	setIsModalRightOpen: React.Dispatch<React.SetStateAction<boolean>>,
	setCompaniesInLasso: React.Dispatch<React.SetStateAction<CompaniesInLasso>>,
	rangeColor: { firstRange: number, lastRange: number },
	onMapMarkerCompanyClicked: (companyId: number, noCloseCluster?: true) => void,
	setClusterPopupState: React.Dispatch<React.SetStateAction<ModalState<ClusterProperties[]>>>
	setModalOptions: React.Dispatch<React.SetStateAction<{ isOpen: boolean, idSelected: number }>>
	modalOptions: { isOpen: boolean, idSelected: number },
	apiData: MapData | undefined,
	minMaxAdditionalColumn: MinMaxAdditionalColumn[]
	minMaxCalculatedFields: MinMaxAdditionalColumn[]
	checkoutsMinMax?: MinMax
	initialPosition?: Position
	convertCompany?: (data: GooglePlaceItem[]) => void
}

export default function ReactMap(props: MapProps) {
	return <MapSettingsProvider><_Map {...props} /></MapSettingsProvider>;
}

function _Map(props: MapProps) {
	const { points, tabType, companiesInLasso, statuses, initialPosition, setIsModalRightOpen, setCompaniesInLasso, rangeColor, onMapMarkerCompanyClicked, setClusterPopupState, setModalOptions, modalOptions } = props;
	const statusMap = React.useMemo(() => statuses.reduce<Map<number, CompanyStatus>>((acc, status) => {
		acc.set(status.id, status);
		return acc;
	}, new Map<number, CompanyStatus>()), [statuses]);
	const profile = useRecoilValue(AProfile);
	const [currentPosition, setCurrentPosition] = React.useState<Position | undefined>(initialPosition ? initialPosition : (profile && profile.latitude && profile.longitude && profile.use_address_as_default_map_center) ? { lat: profile.latitude, lng: profile.longitude } : undefined);
	const [mapState, setMapState] = React.useState<MapState>({ zoom: initialPosition ? initialPositionZoom : defaultZoom, bounds: [], center: currentPosition ?? defaultCenter });
	const [maps, setMaps] = React.useState<any>(null);
	const [map, setMap] = React.useState(null);
	const [lasso, setLasso] = React.useState(null);
	const [addingToLassoOnClick, setAddingToLassoOnClick] = React.useState<boolean>(false);
	const selectedFrequencyEventType = useRecoilValue(AFrequencyEventType);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const [ownersWithCenter, setOwnersWithCenter] = React.useState<UserCenter[]>([]);
	const users = useRecoilValue(AUsers);
	const userMap = React.useMemo(() => {
		return users.reduce<Map<number, Owner>>((acc, user) => {
			acc.set(user.id, user);
			return acc;
		}, new Map());
	}, [users]);
	const [key, setKey] = React.useState<number>(0);
	const [tileLoaded, setTileLoaded] = React.useState<boolean>(false);
	const [googlePlaces, setGooglePlaces] = React.useState<GooglePlaceItem[]>([]);

	const settingContext = React.useContext(MapSettingsContext);
	const {
		userAdressesModeHook,
		displayCompaniesHook,
		mapStyleHook,
	} = settingContext;
	const [userAdressesMode] = userAdressesModeHook;
	const [displayCompanies] = displayCompaniesHook;
	const [mapStyle] = mapStyleHook;
	const { isAllowedTo } = React.useContext(PermissionContext);

	React.useEffect(() => {
		if (currentPosition) {
			setMapState((mapState) => ({ ...mapState, center: currentPosition }));
		}
	}, [currentPosition]);

	React.useEffect(() => {
		if (!initialPosition && !profile.latitude && !profile.latitude && !profile.use_address_as_default_map_center)
			navigator.geolocation.getCurrentPosition(
				position => setCurrentPosition({ lat: position.coords.latitude, lng: position.coords.longitude }),
				err => console.error(err)
			);
	}, []);


	// Refresh the map after 1 sec if background not loaded
	React.useEffect(() => {
		const checkState = () => {
			if (!tileLoaded) { setKey((k) => k + 1); }
		};
		const timer = setTimeout(checkState, 1000);
		return () => clearTimeout(timer);
	}, [tileLoaded]);

	React.useEffect(() => {
		setOwnersWithCenter([]);
		if (tabType === 'urchin') {
			setLoadingState(LoadingStateEnum.LOADING);
			if (points && maps && map) {
				if (urchinGeoJsonPath) {
					urchinGeoJsonPath.setMap(null);
					urchinGeoJsonPath = null;
				}
				if (urchinPoints) {
					urchinPoints.forEach((point) => point.setMap(null));
					urchinPoints = null;
				}


				const ownersWithPoints = points.reduce((acc, point) => {
					const ownerId = point.properties.detail.ownerId!;
					if (acc.has(ownerId)) {
						acc.get(ownerId)!.push(point);
					} else {
						acc.set(ownerId, [point]);
					}
					return acc;
				}, new Map<number, Point[]>());

				const localOwnerWithCenter: { id: number, center: Position }[] = [];
				urchinGeoJsonPath = new maps.Data();
				urchinPoints = [];
				const features: object[] = [];
				for (const [ownerId, points] of ownersWithPoints.entries()) {
					if (points.length === 0) continue;
					const user = userMap.get(ownerId);
					let center: { lat: number, lng: number };

					if (user?.latitude && user?.longitude) {
						center = { lat: user.latitude, lng: user.longitude };
					} else {
						center = points.reduce((center, point) => {
							center.lat += point.geometry.coordinates[1];
							center.lng += point.geometry.coordinates[0];
							return center;
						}, { lat: 0, lng: 0 });
						const nb_points = points.length;
						center = { lat: center.lat / nb_points, lng: center.lng / nb_points };
					}
					localOwnerWithCenter.push({ id: ownerId, center: center });
					points.forEach(point => {
						features.push({
							type: 'Feature',
							geometry: {
								type: 'LineString',
								coordinates: [
									[center.lng, center.lat],
									[point.geometry.coordinates[0], point.geometry.coordinates[1]]
								]
							},
							properties: {
								color: user?.color,
								id: point.properties.id
							}
						});
						const circle = new maps.Circle({
							strokeColor: user?.color,
							strokeOpacity: 0.8,
							strokeWeight: 2,
							fillColor: user?.color,
							fillOpacity: 0.8,
							map,
							center: { lat: point.geometry.coordinates[1], lng: point.geometry.coordinates[0] },
							radius: 35,
							clickable: true
						});
						circle.setMap(map);
						circle.addListener('click', () => {
							onMapMarkerCompanyClicked(point.properties.detail.id!, true);
						});
						urchinPoints!.push(circle);
					});
				}
				setOwnersWithCenter(localOwnerWithCenter);
				urchinGeoJsonPath.addGeoJson({
					type: 'FeatureCollection',
					features: features
				});

				urchinGeoJsonPath.setMap(map);
				urchinGeoJsonPath.setStyle(feature => ({
					strokeColor: feature.getProperty('color'),
					strokeOpacity: 0.6,
					strokeWeight: 1,
				}));

				window.setTimeout(() => {
					setLoadingState(LoadingStateEnum.LOADED);
				}, 1000);

			}
		} else {
			if (urchinGeoJsonPath) {
				urchinGeoJsonPath.setMap(null);
				urchinGeoJsonPath = null;
			}
			if (urchinPoints) {
				urchinPoints.forEach((point) => point.setMap(null));
				urchinPoints = null;
			}
		}
	}, [tabType, points]);

	const userAddresses = useUsersAddresses(userAdressesMode);
	useUsersPoly(points, map, maps);
	useBoundariesPolys(points, map, maps);

	React.useEffect(() => {
		if (lasso && maps) {
			points.forEach(point => {
				if (point && point.geometry.coordinates.every(e => e !== undefined)) {
					const marker = new maps.Marker({
						position: { lat: point.geometry.coordinates[1], lng: point.geometry.coordinates[0] },
						map: null
					});
					if (maps.geometry.poly.containsLocation(marker.getPosition(), lasso)) {
						const index = companiesInLasso.companies.findIndex(c => c.id == point.properties.detail.id);
						if (index >= 0) {
							companiesInLasso.companies[index] = point.properties.detail as InitialMapRow;
						} else {
							companiesInLasso.companies.push(point.properties.detail as InitialMapRow);
						}
					}
				}
			});
			setIsModalRightOpen(true);
			const googleInLasso = new Set(companiesInLasso.googlePlacesItems.map(item => item.id));
			googlePlaces.forEach(gp => {
				if (googleInLasso.has(gp.id)) return;
				const marker = new maps.Marker({
					position: { lat: gp.location.latitude, lng: gp.location.longitude },
					map: null
				});
				if (!maps.geometry.poly.containsLocation(marker.getPosition(), lasso)) return;
				companiesInLasso.googlePlacesItems.push(gp);
				googleInLasso.add(gp.id);

			});
			setCompaniesInLasso(c => ({ ...c }));

		}
	}, [lasso]);

	const geometryPoints = React.useMemo(() => points.map(p => ({
		...p,
		geometry: {
			coordinates: [p.geometry.coordinates[0] ?? 0, p.geometry.coordinates[1] ?? 0]
		}
	})), [points]);

	const { clusters } = useSupercluster({
		points: geometryPoints,
		bounds: mapState.bounds,
		zoom: mapState.zoom,
		options: {
			radius: 60,
			minZoom: 3,
			maxZoom: 24,
			nodeSize: 64,
			extent: 512,
			log: false,
			generate: false,
			minPoints: 2,
			map: (p: { detail: MapCompany }): { details: ClusterProperties[] } => ({
				details: [{
					id: p.detail.id,
					owner: p.detail.ownerId,
					status: p.detail.clientStatusId,
					statusColor: p.detail.colorCode,
					// events
					range: p.detail.range,
					nextRange: p.detail.nextRange,
					next: p.detail.next,
					campaign: p.detail.campaignId,
					futureCampaign: p.detail.futureCampaignId,
					// distribution
					distribution: p.detail.distribution,
					orderRange: p.detail.orderRange,
					// forms
					formsRange: p.detail.formsRange,
					// frequency
					frequencies: p.detail.frequencies,
					checkouts: p.detail.checkoutEvolution,
					additionalColumnsValue: p.detail.additionalColumnsValue,
					additionalFieldColumnsValue: p.detail.additionalFieldColumnsValue,
					calculatedFieldColumnsValue: p.detail.calculatedFieldColumnsValue
				}]
			}),
			reduce: (acc: { details: ClusterProperties[] }, p: { details: ClusterProperties[] }) => {
				acc.details = acc.details.concat(p.details);
				return acc;
			}
		}
	});

	const canViewGoogleEnrichment = isAllowedTo({ objectAction: 'ViewGoogleEnrichment' });

	const handleApiLoaded = React.useCallback(({ map, maps }) => {
		if (canViewGoogleEnrichment) {
			map.controls[maps.ControlPosition.RIGHT_TOP].insertAt(0, createSearchButton(map, setGooglePlaces, props.convertCompany));
		}
		const fullScreenControl = createCustomFullscreenButton(map);
		map.controls[maps.ControlPosition.RIGHT_TOP].push(fullScreenControl);
		map.controls[maps.ControlPosition.RIGHT_TOP].push(createLayersButton(settingContext));
		const lassoDiv = document.createElement('div');
		createMapButton(lassoDiv, ({ button }) => () => {
			const resetImage = (lassoEnabled: boolean = false) => {
				while (button.firstChild) {
					button.removeChild(button.lastChild!);
				}
				const newImage = document.createElement('div');
				ReactDOM.render(<LassoIcon size={24} color={lassoEnabled ? 'white' : SidelyBlack} />, newImage);
				button.appendChild(newImage);
				button.style.backgroundColor = lassoEnabled ? PrimaryColor : SurfaceColor;
				button.style.border = `2px solid ${lassoEnabled ? PrimaryColor : SurfaceColor}`;
			};
			resetImage(!map.lassoEnabled);
			map.onReleaseLasso = resetImage;
			onLassoClick(map, setLasso);
		}, <LassoIcon size={24} color={map.lassoEnabled ? 'white' : SidelyBlack} />, MAP_ROUND_BUTTON_STYLE, 'Lasso');
		const pinDiv = document.createElement('div');
		createMapButton(pinDiv, ({ button, image }) => () => {
			image.src = !map.addingToLasso ? MapPinWhite : MapPinBlack;
			button.style.backgroundColor = !map.addingToLasso ? PrimaryColor : SurfaceColor;
			button.style.border = `2px solid ${!map.addingToLasso ? PrimaryColor : SurfaceColor}`;
			setAddingToLassoOnClick(!map.addingToLasso);
			map.setOptions({ addingToLasso: !map.addingToLasso });
		}, map.addingToLasso ? MapPinWhite : MapPinBlack, MAP_ROUND_BUTTON_STYLE, 'Pins');
		map.controls[maps.ControlPosition.TOP].push(lassoDiv);
		map.controls[maps.ControlPosition.TOP].push(pinDiv);
		activateMapListeners(map, maps, setMaps, setLasso);
		setMap(map);
		maps.event.addListenerOnce(map, 'tilesloaded', () => {
			setTileLoaded(true);
		});
	}, []);

	const night = MAP_STYLE_LIST[mapStyle]?.[1] ?? false;

	const onClusterCliked = React.useCallback((data: ClusterProperties[]) => {
		setClusterPopupState({ isOpen: true, data });
		const url = new URL(window.location.href);
		url.searchParams.delete('company');
		window.history.pushState({}, '', url);
		setModalOptions(modalLeftOptions => ({ ...modalLeftOptions, isOpen: false }));
	}, []);

	return <>
		<ComponentLoader loadingState={loadingState} allScreen />
		<GoogleMapReact
			key={key}
			bootstrapURLKeys={GOOGLE_KEY}
			defaultCenter={defaultCenter}
			center={mapState.center}
			defaultZoom={defaultZoom}
			zoom={mapState.zoom}
			options={React.useCallback((_maps) => ({
				fullscreenControl: false,
				zoomControl: false,
				styles: MAP_STYLE_LIST[mapStyle][0] ?? MapStyleDefault
			}), [mapStyle])}
			onChange={React.useCallback((val) => {
				if (!val.size || val.size.width === 0) {
					setKey((k) => k + 1);
				}
				setMapState({
					zoom: val.zoom,
					bounds: [
						val.bounds.nw.lng,
						val.bounds.se.lat,
						val.bounds.se.lng,
						val.bounds.nw.lat
					],
					center: val.center
				});
			}, [])}
			yesIWantToUseGoogleMapApiInternals
			onGoogleApiLoaded={handleApiLoaded}
		>
			{tabType !== 'urchin' && displayCompanies && clusters.map((cluster: Cluster | Point) => {
				const [longitude, latitude] = cluster.geometry.coordinates;
				if (isCluster(cluster)) {
					return <Marker
						key={`cluster-${cluster.id}`}
						lat={latitude}
						lng={longitude}
					>
						<ClusterIcon
							companiesInLasso={companiesInLasso}
							night={night}
							points={points}
							pointCount={cluster.properties.point_count}
							eventType={selectedFrequencyEventType}
							properties={cluster.properties.details}
							tabType={tabType}
							rangeColor={rangeColor}
							users={userMap}
							statuses={statusMap}
							onClusterClicked={onClusterCliked}
							mapData={props.apiData}
							minMaxAdditionalColumn={props.minMaxAdditionalColumn}
							minMaxCalculatedFields={props.minMaxCalculatedFields}
							checkoutsMinMax={props.checkoutsMinMax}
						/>
					</Marker>;
				} else {
					return <Marker key={`crime-${cluster.properties.id}`} lat={latitude} lng={longitude}>
						<MapPinComponent
							cluster={cluster}
							companiesInLasso={companiesInLasso}
							modalOptions={modalOptions}
							tabType={tabType}
							rangeColor={rangeColor}
							night={night}
							addingToLassoOnClick={addingToLassoOnClick}
							setIsModalRightOpen={setIsModalRightOpen}
							setCompaniesInLasso={setCompaniesInLasso}
							onMapMarkerCompanyClicked={onMapMarkerCompanyClicked}
							mapData={props.apiData}
							statuses={statusMap}
							userMap={userMap}
							minMaxAdditionalColumn={props.minMaxAdditionalColumn}
							minMaxCalculatedFields={props.minMaxCalculatedFields}
							checkoutsMinMax={props.checkoutsMinMax}
						/>
					</Marker>;
				}
			})}

			{(tabType === 'urchin' ? ownersWithCenter : userAddresses)?.map((owner) => {
				const user = userMap.get(owner.id);
				const userHasPosition = user?.latitude && user?.longitude;
				return <Marker key={`owner-${owner.id}`} lat={owner.center.lat} lng={owner.center.lng}>
					<div style={{ width: '30px', transform: 'translate(-50%, -50%)', filter: `drop-shadow(0px 0px 5px color-mix(in oklab, ${SidelyBlack} 35%, transparent))` }}>
						{!userHasPosition && <div style={{ transform: 'translate(270%, 90%)', height: '7px', width: '7px', backgroundColor: RedSidely, border: '1px solid ' + RedSidely, borderRadius: '50%' }}></div>}
						{user && <Avatar
							style={{ overflow: 'hidden' }}
							width={'30px'}
							name={user.name}
							userId={user.id}
							fontSize={'15px'}
							backgroundColor={user?.color}
						/>}
					</div>
				</Marker>;
			})}
			{googlePlaces.map(place => (
				<MapDot
					key={place.id}
					lat={place.location.latitude}
					lng={place.location.longitude}
					name={place.displayName.text}
					popupTop={20}
					popup={<>
						<div style={{ textAlign: 'center' }}>
							{place.displayName.text}
						</div>
						<NoMarginP style={{ fontSize: 10, marginBottom: 10 }}>{place.formattedAddress}</NoMarginP>
						<DefaultButton margin='0' width='100%' onClick={() => props.convertCompany?.([place])}>
							<Translate id='convert_in_company' />
						</DefaultButton>
					</>}
				>
					<div style={{
						transform: 'translate(-50%, -50%)',
						width: '30px', height: '30px', borderRadius: '50%', backgroundColor: SurfaceColor, border: `2px solid ${SurfaceColor}`,
						boxShadow: `0 0 5px color-mix(in oklab, ${SidelyBlack} 50%, transparent)`, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontSize: '12px'
					}}>
						<img src={googlePinIcon} height={25} />
					</div>
				</MapDot>
			))}
		</GoogleMapReact>
	</>;
}
